import { Analytics, AnalyticsBrowser } from '@segment/analytics-next'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { config } from '../modules/app/config'
import { useImpersonation } from './useCurrentUser'
import { logger } from '../modules/app/logger'
import { getSecondsSinceLogin, useUserAuthPayload } from '../modules/app/auth'
import { parseUTM } from './analyticsUtils'

interface AnalyticsContextState {
    trackAnalyticsEvent: (eventName: string, properties?: any) => void
}

export const AnalyticsContext = React.createContext<AnalyticsContextState>({
    trackAnalyticsEvent: () => {
        return
    },
})

export const AnalyticsProvider = ({ children }: { children: JSX.Element }) => {
    const [analytics, setAnalytics] = useState<Analytics | undefined>(undefined)
    const [UTMParams, setUTMParams] = useState<any>({})
    const [writeKey] = useState(config.segmentKey)
    const location = useLocation()
    const authPayload = useUserAuthPayload()
    const impersonator = useImpersonation()

    const loadAnalytics = useCallback(async () => {
        try {
            const [response] = await AnalyticsBrowser.load(
                { writeKey },
                {
                    integrations: {
                        'Segment.io': {
                            apiHost: 'analytics-api.spritz.finance/v1',
                        },
                    },
                }
            )
            setAnalytics(response)
        } catch (err) {
            logger.exception(err as Error)
        }
    }, [writeKey])

    const trackAnalyticsEvent = useCallback(
        async (eventName: string, properties: any = {}) => {
            if (!analytics) {
                return
            }
            try {
                properties = {
                    ...properties,
                }
                await analytics.track(eventName, properties)
            } catch (err) {
                logger.error('[Analytics error] track error', err)
            }
        },
        [analytics]
    )

    useEffect(() => {
        setUTMParams(parseUTM(location.search))
    }, [])

    useEffect(() => {
        if (analytics) {
            return
        }
        loadAnalytics()
    }, [writeKey, analytics, loadAnalytics])

    useEffect(() => {
        if (!analytics || impersonator) return
        if (!authPayload.userId) return
        try {
            analytics
                .identify(authPayload?.userId, {
                    ...UTMParams,
                })
                .then(() => {})
        } catch (err) {
            logger.error('[Analytics error] identify error', err)
        }
    }, [analytics, authPayload, impersonator])

    useEffect(() => {
        if (!analytics) {
            return
        }
        analytics.page()
    }, [location, analytics, trackAnalyticsEvent])

    return (
        <AnalyticsContext.Provider
            value={{
                trackAnalyticsEvent,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    )
}

export const useAnalytics = () => {
    const { trackAnalyticsEvent } = useContext(AnalyticsContext)
    return {
        trackAnalyticsEvent,
    }
}
